"use client";

import type { Variants } from "framer-motion";
import { motion, useScroll } from "framer-motion";
import {
	CheckCircleIcon,
	LayoutDashboard,
	BarChart as MenuIcon,
	User,
	XIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useId, useState } from "react";
import { AnimatedSection } from "@/components/animated-section";
import LanguageSwitcher from "@/components/common/language-switcher";
import { ThemeSwitcher } from "@/components/common/theme-switcher";
import { FAQSection } from "@/components/faq-section";
import { GridBackground } from "@/components/grid-background";
import { Logo } from "@/components/logo";
import { PricingCards } from "@/components/pricing-card";
import { TestimonialGridSection } from "@/components/testimonial-grid-section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/contexts/auth-context";

// Simple animation presets
const fadeUp: Variants = {
	hidden: { opacity: 0, y: 24 },
	show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger: Variants = {
	hidden: {},
	show: { transition: { staggerChildren: 0.12 } },
};

function FeaturesList() {
	const t = useTranslations("LandingPage");

	return (
		<div className="mt-8 max-w-lg mx-auto space-y-3">
			<div className="flex items-center font-semibold">
				<CheckCircleIcon className="mr-5 h-6 w-6 text-[var(--landing-page-accent)] shrink-0" />
				<span className="text-sm text-[var(--landing-page-accent)] mr-1">
					Free
				</span>
				<span className="text-sm text-muted-foreground">
					{t("hero.stats.trial")}
				</span>
			</div>
			<div className="flex items-center font-semibold">
				<CheckCircleIcon className="mr-5 h-6 w-6 text-[var(--landing-page-accent)] shrink-0" />
				<span className="text-sm  text-[var(--landing-page-accent)] mr-1">
					Fast
				</span>
				<span className="text-sm text-muted-foreground">
					{t("hero.stats.setup")}
				</span>
			</div>
			<div className="flex items-center font-semibold">
				<CheckCircleIcon className="mr-5 h-6 w-6 text-[var(--landing-page-accent)] shrink-0" />
				<span className="text-sm  text-[var(--landing-page-accent)] mr-1">
					Easy
				</span>
				<span className="text-sm text-muted-foreground">
					{t("hero.stats.visibility")}
				</span>
			</div>
		</div>
	);
}

export default function Home() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const { scrollY } = useScroll();
	const router = useRouter();
	const featuresId = useId();
	const howId = useId();
	const pricingId = useId();
	const testimonialsId = useId();
	const faqId = useId();
	const contactId = useId();
	const t = useTranslations("LandingPage");
	const { currentUser } = useAuth();

	useEffect(() => {
		return scrollY.onChange((v) => setIsScrolled(v > 12));
	}, [scrollY]);

	// Get accent color from CSS custom property for component props
	const accentColor = "var(--landing-page-accent)";

	return (
		<div className="min-h-screen text-foreground relative overflow-hidden">
			{/* Gradient Background for non-hero sections */}
			<div
				className="fixed inset-0 z-0"
				style={{
					background: `linear-gradient(135deg, 
						hsl(var(--background)) 0%, 
						hsl(var(--primary) / 0.05) 50%, 
						hsl(var(--primary) / 0.1) 100%
					)`,
				}}
			/>

			{/* Header */}
			<header
				className={`fixed top-0 inset-x-0 z-50 transition-all ${
					isScrolled
						? "bg-background/70 backdrop-blur-md border-b border-border"
						: "bg-transparent"
				}`}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="h-16 flex items-center justify-between">
						<Logo
							textClassName="text-2xl font-bold tracking-tighter"
							iconClassName="h-8 w-8"
							strokeWidth={1.25}
						/>

						<nav className="hidden md:flex items-center gap-8">
							<Link href={`#${pricingId}`} className="hover:text-foreground/80">
								{t("navigation.pricing")}
							</Link>
							<Link
								href={`#${testimonialsId}`}
								className="hover:text-foreground/80"
							>
								{t("navigation.testimonials")}
							</Link>
							<Link href={`#${faqId}`} className="hover:text-foreground/80">
								{t("navigation.faq")}
							</Link>
							<Link href={`#${contactId}`} className="hover:text-foreground/80">
								{t("navigation.contact")}
							</Link>
							<div className="flex items-center gap-2">
								<LanguageSwitcher />
								<ThemeSwitcher />
							</div>
							{currentUser ? (
								<button
									onClick={() => router.push("/dashboard")}
									className="rounded-full px-5 py-2 font-medium shadow-xs flex items-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)]"
									type="button"
								>
									<LayoutDashboard className="w-4 h-4" />
									{t("navigation.dashboard")}
								</button>
							) : (
								<button
									onClick={() => router.push("/login")}
									className="rounded-full px-5 py-2 font-medium shadow-xs flex items-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)]"
									type="button"
								>
									<User className="w-4 h-4" />
									{t("navigation.login")}
								</button>
							)}
						</nav>

						<div className="md:hidden p-2 rounded-md hover:bg-foreground/5 flex items-center gap-2">
							<LanguageSwitcher />
							<ThemeSwitcher />
							<button
								aria-label={t("navigation.openMenu")}
								onClick={() => setMobileOpen((s) => !s)}
								type="button"
							>
								{mobileOpen ? (
									<XIcon className="w-6 h-6" />
								) : (
									<MenuIcon className="w-6 h-6" />
								)}
							</button>
						</div>
					</div>
				</div>

				{/* Mobile menu */}
				{mobileOpen && (
					<div className="md:hidden border-t border-border bg-background/90 backdrop-blur-xs">
						<div className="mx-auto max-w-7xl px-4 py-4 space-y-2">
							<Link
								href={`#${pricingId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								{t("navigation.pricing")}
							</Link>
							<Link
								href={`#${testimonialsId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								{t("navigation.testimonials")}
							</Link>
							<Link
								href={`#${faqId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								{t("navigation.faq")}
							</Link>
							<Link
								href={`#${contactId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								{t("navigation.contact")}
							</Link>
							{currentUser ? (
								<button
									onClick={() => router.push("/dashboard")}
									className="w-full rounded-full px-5 py-2 font-medium flex items-center justify-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)]"
									type="button"
								>
									<LayoutDashboard className="w-4 h-4" />
									{t("navigation.dashboard")}
								</button>
							) : (
								<button
									onClick={() => router.push("/login")}
									className="w-full rounded-full px-5 py-2 font-medium flex items-center justify-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)]"
									type="button"
								>
									<User className="w-4 h-4" />
									{t("navigation.login")}
								</button>
							)}
						</div>
					</div>
				)}
			</header>

			{/* Hero */}
			<AnimatedSection className="relative overflow-hidden pt-25 z-10">
				{/* Grid Background - Hero Only */}
				<GridBackground className="absolute inset-0 z-0" opacity={0.08} />

				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-20">
					<motion.div
						variants={stagger}
						initial="hidden"
						animate="show"
						className="text-center max-w-4xl mx-auto"
					>
						<motion.div variants={fadeUp}>
							<Link href="https://www.youtube.com" target="_blank">
								<Badge
									variant="secondary"
									className="mb-4 cursor-pointer bg-[var(--landing-page-accent)]/8 text-sm font-semibold"
								>
									{t("hero.badge")}
								</Badge>
							</Link>
							<h1
								className="tracking-normal text-4xl sm:text-5xl lg:text-5xl font-semibold line-height
								leading-tight break-words"
							>
								{t("hero.title")}
							</h1>
							<p className="mt-5 px-10 sm:text-lg text-muted-foreground max-w-2xl mx-auto">
								{t("hero.description")}
							</p>
							<div className="mt-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-3 justify-center">
								<Link href="/login">
									<Button
										className="text-base rounded-full px-6 py-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)] font-semibold"
										type="button"
									>
										<User className="w-4 h-4" />
										{t("hero.getStarted")}
									</Button>
								</Link>
								<Link
									href="https://calendly.com/koutar-mohamed/30min"
									target="_blank"
								>
									<Button
										className="text-base rounded-full px-6 py-6 border border-border hover:border-border/80 transition"
										variant="outline"
										type="button"
									>
										{t("hero.scheduleDemo")}
									</Button>
								</Link>
							</div>
							<div>
								<div className="flex justify-center">
									<FeaturesList />
								</div>
							</div>
						</motion.div>
					</motion.div>

					{/* New Hero Visuals */}
					<motion.div
						variants={fadeUp}
						initial="hidden"
						animate="show"
						transition={{ delay: 0.8, duration: 0.8 }}
						className="relative mt-5"
					>
						{/* Main Dashboard Screenshot */}
						<div className=" rounded-lg overflow-hidden relative -mb-10 ">
							<Image
								src="/landing-page/dashboard-light.png"
								alt="BRIK Dashboard Screenshot"
								width={1084}
								height={600}
								className="m-auto h-full sm:pt-8 rounded-2xl border border-border/10"
								priority
							/>
						</div>
					</motion.div>
				</div>
			</AnimatedSection>

			{/* Features */}
			{/* <AnimatedSection
				id={featuresId}
				className="py-20 relative z-10"
				delay={0.01}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							{t("features.title")}
						</h2>
						<p className="mt-3 text-muted-foreground">
							{t("features.description")}
						</p>
					</div>

					<motion.div
						variants={stagger}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, margin: "-80px" }}
						className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
					>
						<FeatureCard
							icon={<TrendingUp className="w-5 h-5" />}
							title={t("features.items.financial.title")}
							description={t("features.items.financial.description")}
							illustration={<FinancialTrackingIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
						<FeatureCard
							icon={<CalendarCheck2 className="w-5 h-5" />}
							title={t("features.items.schedule.title")}
							description={t("features.items.schedule.description")}
							illustration={<ScheduleIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
						<FeatureCard
							icon={<UsersIcon className="w-5 h-5" />}
							title={t("features.items.collaboration.title")}
							description={t("features.items.collaboration.description")}
							illustration={<CollaborationIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
						<FeatureCard
							icon={<ShieldCheck className="w-5 h-5" />}
							title={t("features.items.compliance.title")}
							description={t("features.items.compliance.description")}
							illustration={<ComplianceIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
						<FeatureCard
							icon={<PieChart className="w-5 h-5" />}
							title={t("features.items.reporting.title")}
							description={t("features.items.reporting.description")}
							illustration={<ReportingIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
						<FeatureCard
							icon={<LineChart className="w-5 h-5" />}
							title={t("features.items.analytics.title")}
							description={t("features.items.analytics.description")}
							illustration={<AnalyticsIllustration />}
							variants={fadeUp}
							accentColor={accentColor}
						/>
					</motion.div>
				</div>
			</AnimatedSection> */}

			{/* Process */}
			{/* <AnimatedSection
				id={howId}
				className="py-20 bg-muted/30 relative z-10"
				delay={0.01}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							{t("process.title")}
						</h2>
						<p className="mt-3 text-muted-foreground">
							{t("process.description")}
						</p>
					</div>

					<div className="mt-12 grid md:grid-cols-3 gap-6">
						{[
							t("process.steps.plan"),
							t("process.steps.execute"),
							t("process.steps.report"),
						].map((title, i) => (
							<div
								key={title}
								className="relative rounded-2xl border border-border bg-card p-6 shadow-xs"
							>
								<div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)]">
									{i + 1}
								</div>
								<h3 className="mt-4 text-xl font-semibold">{title}</h3>
								<p className="mt-2 text-sm text-muted-foreground">
									{i === 0 && t("process.descriptions.plan")}
									{i === 1 && t("process.descriptions.execute")}
									{i === 2 && t("process.descriptions.report")}
								</p>
							</div>
						))}
					</div>
				</div>
			</AnimatedSection> */}

			{/* Testimonials */}
			<AnimatedSection
				id={testimonialsId}
				className="py-20 relative z-10"
				delay={0.1}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							{t("testimonials.title")}
						</h2>
						<p className="mt-3 text-muted-foreground">
							{t("testimonials.description")}
						</p>
					</div>

					<div className="mt-12">
						<TestimonialGridSection />
					</div>
				</div>
			</AnimatedSection>

			{/* Pricing */}
			<AnimatedSection
				id={pricingId}
				className="py-20 bg-muted/30 relative z-10"
				delay={0.1}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<motion.div
						variants={stagger}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, margin: "-80px" }}
						className="text-center max-w-3xl mx-auto mb-8"
					>
						<motion.h2
							variants={fadeUp}
							className="text-4xl sm:text-6xl font-semibold"
						>
							{t("pricing.title")}
						</motion.h2>
						<motion.p variants={fadeUp} className="mt-3 text-xl px-5">
							{t("pricing.description")}
						</motion.p>
					</motion.div>

					<motion.div
						variants={fadeUp}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, margin: "-80px" }}
					>
						<PricingCards />
					</motion.div>
				</div>
			</AnimatedSection>

			{/* Final CTA */}
			<AnimatedSection
				id={contactId}
				className="py-10 relative z-10"
				delay={0.1}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-muted/60 to-muted/20 p-8 sm:p-12 text-center">
						<div
							className="absolute -inset-1 rounded-3xl opacity-20 blur-2xl"
							style={{
								background: `radial-gradient(closest-side, var(--landing-page-accent), transparent)`,
							}}
						/>
						<h2 className="relative text-3xl sm:text-4xl font-semibold">
							{t("cta.title")}
						</h2>
						<p className="relative mt-3 text-muted-foreground max-w-2xl mx-auto">
							{t("cta.description")}
						</p>
						<div className="relative mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
							<Link href="/login" className="w-full sm:w-auto">
								<Button
									className="font-semibold text-base rounded-full px-6 py-6 flex items-center justify-center gap-2 bg-[var(--landing-page-accent)] text-[var(--landing-page-accent-foreground)] w-full sm:w-auto"
									type="button"
								>
									<User className="size-5!" />
									{t("cta.login")}
								</Button>
							</Link>
							<Link
								href="https://calendly.com/koutar-mohamed/30min"
								target="_blank"
								className="w-full sm:w-auto"
							>
								<Button
									className="text-base rounded-full px-6 py-6 border border-border hover:border-border/80 transition w-full sm:w-auto justify-center"
									type="button"
									size={"lg"}
									variant={"outline"}
								>
									{t("hero.scheduleDemo")}
								</Button>
							</Link>
						</div>
					</div>
				</div>
			</AnimatedSection>

			{/* FAQ Section */}
			<AnimatedSection
				id={faqId}
				className="bg-muted/30 relative z-10"
				delay={0.1}
			>
				<FAQSection />
			</AnimatedSection>

			{/* Footer */}
			<AnimatedSection
				className="border-t border-border py-10 relative z-10"
				delay={0.1}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col md:flex-row items-center justify-between gap-6">
						<div className="flex items-center gap-3">
							<Image
								src="/logo-1.png"
								alt="BRIK"
								width={120}
								height={32}
								className="h-6 w-auto dark:brightness-0 dark:invert"
							/>
							<span className="text-muted-foreground text-sm">
								© {new Date().getFullYear()} BRIK
							</span>
						</div>
						<nav className="grid grid-cols-2 gap-x-40 gap-y-4 sm:flex sm:flex-row sm:items-center sm:gap-6 text-sm text-muted-foreground">
							<Link href="#features" className="hover:text-foreground">
								{t("footer.features")}
							</Link>
							<Link href="#how" className="hover:text-foreground">
								{t("footer.process")}
							</Link>
							<Link href="#pricing" className="hover:text-foreground">
								{t("footer.pricing")}
							</Link>
							<Link href={`#${faqId}`} className="hover:text-foreground">
								{t("footer.faq")}
							</Link>
							<Link href="#contact" className="hover:text-foreground">
								{t("footer.contact")}
							</Link>
							<Link href="/privacy" className="hover:text-foreground">
								{t("footer.privacy")}
							</Link>
							<Link href="/terms" className="hover:text-foreground">
								{t("footer.terms")}
							</Link>
						</nav>
					</div>
				</div>
			</AnimatedSection>
		</div>
	);
}
